// Per spec/components.md §4. Photo-led card used by `event-card-group`.
//
// **P-037.22 / D-130 rework.** New layout per Banks's PSD audit:
// - 10 px solid navy bar above the image (`accent-navy-deep` #041e42).
// - Image fixed at 190 × 100 (centered). The prior `imageWidth?` override
//   is gone — `event-card-group`'s 3-col grid is the only consumer and
//   it no longer varies card width per option.
// - Title: 16 px Helvetica bold `accent-navy-deep`. The heading primitive's
//   default color is `accent-navy-deep` post-D-118, so passing `color`
//   explicitly is documentation rather than a render-output change.
// - Date + optional location render as a single 14 px text block with
//   `<br>` separator (single `<p>` via the text primitive's HtmlString-
//   on-text path). No separate location row.
// - 14 px gaps between title → date/location → CTA (was 4 + 16).
// - CTA: 16 px text-style (text or link), no `button(...)` arm. Mirrors
//   events-list (EventRow) — three CTA arms by ctaLabel + ctaUrl
//   presence:
//   - No CTA when `ctaLabel === undefined`.
//   - Link arm when `ctaUrl !== undefined` — anchor at the template-
//     level `linkColor` (P-037.18 D-118 — defaults to
//     `tokens.color['link-default']`).
//   - Text arm when `ctaUrl === undefined` — black `#000000` plain text
//     (matches event-row's CTA_TEXT_COLOR per D-117).
// - Bottom spacer kept at 16 px for card-bottom breathing.
//
// The Block's `event-card-group` schema constrains `ctaLabel` to the
// `Register Now | Add to Waitlist | Save the Date` enum (same as
// events-list per D-115); the Component is enum-agnostic — it dispatches
// purely on the `ctaUrl` presence and renders whatever string the Block
// passes.
//
// Discriminated-union props relaxed (mirrors EventRow per D-115): the
// `ctaLabel + ctaUrl` paired-required constraint is replaced with
// `ctaLabel set ⇒ ctaUrl optional`. The Block-layer mapper chooses the
// final arm and `pickDefined`s the `ctaUrl` accordingly.
//
// **P-037.23 / D-131 — image optional.** `imageUrl` is optional: when
// absent, the image slot renders the chapter fallback PNG
// (`/fallback-image.png`, 190 × 100, decorative `alt=""`). The 10 px
// navy bar above the image is unaffected by the image-set/unset choice.
//
// **P-037.29 / D-137 §item 4 (revised) — image-cell edge: inset shadow
// on the cell.** Reverts the D-133 1 px IMG border. The cell carries
// `box-shadow: inset 0 0 16px rgba(0, 0, 0, 0.35);` (no padding ring,
// no IMG-shrink — Banks's correction post-initial-D-137); the IMG
// renders at full 190 × 100 edge-to-edge. The shadow is mostly hidden
// behind full-bleed staff images (same as the original D-131 §13
// state) but renders where the image has transparent edges. For
// composite cards (Big3EventCard composite arm + big-3-hero-image),
// the inset shadow is baked into the PNG itself at composite time so
// it's always visible — see `src/lib/image-composite.ts`. Outlook
// desktop drops `box-shadow` silently (progressive enhancement).

import { html } from '../render'
import { tokens } from '../tokens'
import { heading } from '../primitives/heading'
import { text } from '../primitives/text'
import { image } from '../primitives/image'
import { link } from '../primitives/link'
import { spacer } from '../primitives/spacer'
import { HtmlString } from '../types'
import { FALLBACK_IMAGE_SRC } from './helpers'

interface EventCardBase {
  title: string
  dateText: string
  location?: string
  imageUrl?: string
  imageAlt?: string
  /** Template-level link color from `RenderContext.linkColor` (P-037.18
   *  D-118). Used as the link-arm CTA color. Defaults to
   *  `tokens.color['link-default']`. The text-arm CTA color is always
   *  black per D-117 — definitive label styling rather than link
   *  styling. */
  linkColor?: string
}

export type EventCardProps =
  | (EventCardBase & { ctaLabel?: undefined; ctaUrl?: undefined })
  | (EventCardBase & { ctaLabel: string; ctaUrl?: string })

// P-037.29 D-137 §item 4 (revised at post-Phase-Close §item G): IMG
// renders at full card dimensions (190 × 100). Inset shadow is baked
// into the image bytes at upload time via `bakeInsetShadow: true` on
// the registry entry (`event-card-group.events.imageUrl`); fallback
// arm gets shadow from the pre-baked `/fallback-image.png` static
// asset (baked once at §item G time, matches the upload-pipeline
// algorithm). No cell-level CSS shadow needed — fully redundant for
// both arms.
const IMAGE_WIDTH = 190
const IMAGE_HEIGHT = 100
const NAVY_BAR_HEIGHT = 10
const CARD_PAD_X = tokens.space[4]
const IMAGE_TO_TITLE_GAP = tokens.space[4]
const SECTION_GAP_14 = 14
const CTA_TEXT_COLOR = '#000000'
const NAVY = tokens.color['accent-navy-deep']
const BOTTOM_SPACER = spacer({ height: tokens.space[4] })

export function eventCard(props: EventCardProps): HtmlString {
  // 10 px navy strip flush above image — bulletproof image-flush attrs so
  // no implicit cell whitespace renders inside the strip in any client.
  //
  // P-048.5 / D-148 — wrap the bar in a 190px-wide centered inner table
  // so the bar matches the image width below on mobile collapse. The
  // outer card `<table width="100%">` resolves to 190px on desktop (card
  // cell width) but ~310px on mobile (after `@media (max-width: 480px)`
  // forces card-cell to 100% width). The image inside stays fixed at
  // `width: 190px`, so the bar visibly extended past the image's right
  // edge in Banks's Litmus Android Outlook preview. Three-layer belt-
  // and-suspenders centering: inner `<table align="center">` (Outlook
  // desktop Word renderer) + outer `<td align="center">` (fallback) +
  // inner-table inline `margin: 0 auto;` (modern clients ignoring HTML
  // align=). Same pattern as D-147 §A banner right-align, mirrored to
  // center instead.
  // prettier-ignore
  const navyBarRow = html`<tr><td align="center" style="padding: 0; font-size: 0; line-height: 0;"><table role="presentation" width="${IMAGE_WIDTH}" cellpadding="0" cellspacing="0" border="0" align="center" style="margin: 0 auto;"><tr><td height="${NAVY_BAR_HEIGHT}" bgcolor="${NAVY}" style="line-height: ${NAVY_BAR_HEIGHT}px; font-size: 0;">&nbsp;</td></tr></table></td></tr>`
  // P-037.23 D-131: when `imageUrl` is undefined, render the chapter
  // fallback PNG with decorative `alt=""` (the title supplies the
  // semantic label below). When defined, follows the prior D-130 shape:
  // staff image + alt fallback to title.
  const imageHtml =
    props.imageUrl !== undefined
      ? image({
          src: props.imageUrl,
          alt: props.imageAlt ?? props.title,
          width: IMAGE_WIDTH,
          height: IMAGE_HEIGHT,
          align: 'center',
        })
      : image({
          src: FALLBACK_IMAGE_SRC,
          alt: '',
          width: IMAGE_WIDTH,
          height: IMAGE_HEIGHT,
          align: 'center',
        })
  // 16 px bold navy title. `weight` + `size` are non-default; `color` is
  // the heading primitive's post-D-118 default but passed explicitly for
  // readability at this non-default 16 px size.
  const titleHtml = heading({
    text: props.title,
    level: 3,
    size: tokens.type.size.base,
    weight: tokens.type.weight.bold,
    color: NAVY,
  })
  // Date + optional location as a single 14 px text block with <br>
  // separator. Single <p> via the text primitive's HtmlString-on-text
  // path; html`` kernel escapes the interpolated strings and passes the
  // literal `<br>` through.
  // prettier-ignore
  const dateLocText: string | HtmlString =
    props.location !== undefined ? html`${props.dateText}<br>${props.location}` : props.dateText
  // P-037.31 third post-Phase-Close §B — date+location dropped 14 → 12 px
  // (`sm` → `xs`). Banks's audit pick — uniform across all EventCard
  // consumers (event-card-group + big3-signature-events signature arm).
  const dateLocHtml = text({ text: dateLocText, size: tokens.type.size.xs })
  // CTA dispatch — three arms by (ctaLabel, ctaUrl) presence:
  // - undefined → no CTA row.
  // - ctaUrl set → link arm at template-level color.
  // - ctaUrl unset → plain text arm in black (text-fallback or
  //   "Save the Date" label).
  let ctaHtml: HtmlString | undefined
  if (props.ctaLabel !== undefined) {
    const ctaLinkColor = props.linkColor ?? tokens.color['link-default']
    ctaHtml =
      props.ctaUrl !== undefined
        ? text({
            text: link({
              text: props.ctaLabel,
              href: props.ctaUrl,
              color: ctaLinkColor,
              weight: tokens.type.weight.bold,
            }),
          })
        : text({
            text: props.ctaLabel,
            weight: tokens.type.weight.bold,
            color: CTA_TEXT_COLOR,
          })
  }
  // prettier-ignore
  const ctaRow = ctaHtml !== undefined
    ? html`<tr><td style="padding: ${SECTION_GAP_14}px ${CARD_PAD_X}px 0;">${ctaHtml}</td></tr>`
    : undefined

  // P-048.6 §B / D-149 — outer card table fixed at IMAGE_WIDTH (190) +
  // centered. Was `width="100%"` pre-P-048.6 which expanded to ~310 px
  // on mobile (after the document-shell `@media (max-width: 480px) {
  // td[width] { width: 100% !important; display: block !important } }`
  // rule collapsed the parent grid cell to 100% column width). The
  // outer card's `width="100%"` then matched the wider parent, while
  // the image inside stayed px-fixed at 190 — bar centered (post-P-048.5)
  // but image flush-left → visible misalignment in Banks's Litmus
  // Android Outlook preview. Fix: pin the outer card at width=190 so
  // the entire card stays 190 wide on mobile + center it in the
  // 100%-wide parent cell via `align="center"` + `margin: 0 auto;`.
  // The mobile @media rule targets `td[width]` only, NOT `table[width]`
  // — the outer card table survives the collapse unaffected. Belt-
  // and-suspenders: HTML `align=` (Outlook desktop Word renderer +
  // Apple Mail) + inline CSS `margin: 0 auto` (modern clients
  // ignoring HTML `align=`). The P-048.5 inner 190-wide centered
  // table around the navy bar stays as additional belt-and-suspenders
  // (Banks-confirmed at P-048.6 session opening per
  // [[feedback_questions_one_at_a_time]] — keep both layers).
  // prettier-ignore
  return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="${IMAGE_WIDTH}" align="center" style="margin: 0 auto;">${navyBarRow}<tr><td>${imageHtml}</td></tr><tr><td style="padding: ${IMAGE_TO_TITLE_GAP}px ${CARD_PAD_X}px 0;">${titleHtml}</td></tr><tr><td style="padding: ${SECTION_GAP_14}px ${CARD_PAD_X}px 0;">${dateLocHtml}</td></tr>${ctaRow}${BOTTOM_SPACER}</table>`
}
